// Localization (English + Italian). UI chrome lives in CHROME; content names and
// descriptions live in C[cat][id]. All lookups fall back to the English text/fn
// passed by the caller, so missing translations never break the game.
// localStorage access is guarded so headless tests (node, no DOM) stay green.

export type Lang = 'en' | 'it'

let _lang: Lang = 'en'
try {
  if (typeof localStorage !== 'undefined') {
    const s = localStorage.getItem('whackhalla.lang')
    if (s === 'it' || s === 'en') _lang = s
  }
} catch {
  /* ignore */
}

export function getLang(): Lang {
  return _lang
}
export function setLang(l: Lang): void {
  _lang = l
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem('whackhalla.lang', l)
  } catch {
    /* ignore */
  }
}
export function toggleLang(): Lang {
  setLang(_lang === 'en' ? 'it' : 'en')
  return _lang
}
export function langLabel(): string {
  return _lang === 'en' ? 'Italiano' : 'English'
}

// CO1 — original boss epithets shown as the intro-card subtitle ([en, it]).
const BOSS_TITLES: Record<string, [string, string]> = {
  boss_golem: ['THE MOUNTAIN AWOKEN', 'LA MONTAGNA DESTA'],
  boss_hydra: ['MANY-HEADED DOOM', 'ROVINA DALLE MILLE TESTE'],
  boss_lich: ['MIND OF THE SWARM', 'MENTE DELLO SCIAME'],
  boss_queen: ['THE HIVE SOVEREIGN', "LA SOVRANA DELL'ALVEARE"],
  boss_warlord: ['SCOURGE OF THE WASTES', 'FLAGELLO DELLE LANDE'],
  boss_rootzilla: ['THE DEVOURING GROVE', 'IL BOSCO DIVORATORE'],
  boss_sandwyrm: ['TERROR BENEATH THE DUNES', 'IL TERRORE SOTTO LE DUNE'],
  boss_reaper: ['HARVESTER OF SOULS', 'MIETITORE DI ANIME'],
  boss_forklift: ['INDUSTRIAL NIGHTMARE', 'INCUBO INDUSTRIALE'],
  boss_scorpion: ['THE VENOM CROWN', 'LA CORONA DI VELENO'],
  boss_fridge: ['THE COLD ENGINE', 'IL MOTORE GELIDO'],
  boss_djskel: ['LORD OF THE LAST BEAT', "SIGNORE DELL'ULTIMO RITMO"],
  boss_voidlord: ['HERALD OF THE GREAT DARK', 'ARALDO DEL GRANDE BUIO'],
}
export function bossTitle(id: string): string {
  const e = BOSS_TITLES[id]
  return e ? e[_lang === 'it' ? 1 : 0] : ''
}

// ---- UI chrome: [en, it] ----
const CHROME: Record<string, [string, string]> = {
  play: ['PLAY', 'GIOCA'],
  shrine_btn: ['SHRINE', 'SANTUARIO'],
  installApp: ['INSTALL APP', 'INSTALLA APP'],
  installTitle: ['Play fullscreen', 'Gioca a schermo intero'],
  installIosSteps: [
    'Tap the Share button below, then choose "Add to Home Screen" to install WHACKHALLA and play fullscreen.',
    'Tocca il pulsante Condividi in basso, poi scegli "Aggiungi a Home" per installare WHACKHALLA e giocare a schermo intero.',
  ],
  installManualSteps: [
    'Open your browser menu and choose "Install app" / "Add to Home screen" to play WHACKHALLA fullscreen.',
    'Apri il menu del browser e scegli "Installa app" / "Aggiungi a schermata Home" per giocare a WHACKHALLA a schermo intero.',
  ],
  sysOnline: ['SYS ONLINE', 'SISTEMI ONLINE'],
  linkStable: ['LINK STABLE', 'LINK STABILE'],
  hint_menu: [
    'WASD move · drag / Q·E = rotate cam · weapons auto-fire · Space = jump · Shift = slide · ESC = pause',
    'WASD muovi · trascina / Q·E = ruota camera · armi automatiche · Spazio = salto · Shift = scivolata · ESC = pausa',
  ],
  bank: ['Bank', 'Banca'],
  best: ['Best', 'Record'],
  runs: ['Runs', 'Partite'],
  chooseChampion: ['Choose your champion', 'Scegli il tuo campione'],
  champion_sub: [
    'Each hero plays differently and starts with a unique weapon.',
    'Ogni eroe gioca in modo diverso e parte con un’arma unica.',
  ],
  startsWith: ['Starts with', 'Inizia con'],
  back: ['← Back', '← Indietro'],
  chooseBattlefield: ['Choose your battlefield', 'Scegli il campo di battaglia'],
  battlefield_sub: [
    'Higher intensity = more enemies, more danger, more loot.',
    'Più intensità = più nemici, più pericolo, più bottino.',
  ],
  intensity: ['Intensity', 'Intensità'],
  shrineTitle: ['The Shrine', 'Il Santuario'],
  shrine_sub: [
    'Spend banked gold on permanent upgrades that apply to every run.',
    'Spendi l’oro in banca per potenziamenti permanenti, validi in ogni partita.',
  ],
  resetSave: ['Reset save', 'Azzera salvataggio'],
  maxed: ['MAXED', 'MAX'],
  levelUp: ['LEVEL UP!', 'LIVELLO SU!'],
  chooseUpgrade: ['Choose an upgrade', 'Scegli un potenziamento'],
  queued: ['{0} level-ups queued', '{0} livelli in coda'],
  reroll: ['Reroll · {0}', 'Ripesca · {0}'],
  skipUp: ['Skip · +25', 'Salta · +25'],
  togglerTitle: ['Draft Pool', 'Pool del Draft'],
  toggler_sub: ['Disable weapons, items or tomes you never want offered — keeps the draft focused on what you like.', 'Disabilita armi, oggetti o tomi che non vuoi mai pescare — mantiene il draft sulle cose che ti piacciono.'],
  togglerBtn: ['Draft Pool', 'Pool del Draft'],
  itemEnabled: ['ON', 'ON'],
  itemDisabled: ['OFF', 'OFF'],
  left: ['{0} left', '{0} rimasti'],
  vendorTitle: ['Traveling Vendor', 'Mercante errante'],
  shopReroll: ['Reroll · {0}', 'Ripesca · {0}'],
  vendor_sub: [
    'Spend gold mid-run — limited stock. Grab what you need and go.',
    'Spendi oro durante la partita — scorte limitate. Prendi e vai.',
  ],
  leave: ['Leave', 'Vai via'],
  sold: ['SOLD', 'VENDUTO'],
  heartyMeal: ['Hearty Meal', 'Pasto abbondante'],
  heartyMealDesc: ['Restore 50% of your max HP.', 'Recupera il 50% degli HP massimi.'],
  banishCharge: ['Banish Charge', 'Carica Bandisci'],
  banishChargeDesc: ['+1 banish for the level-up draft.', '+1 bandisci per la scelta dei potenziamenti.'],
  paused: ['Paused', 'In pausa'],
  resume: ['Resume', 'Riprendi'],
  quitRun: ['Quit run', 'Abbandona'],
  confirmTitle: ['Are you sure?', 'Sei sicuro?'],
  cancelBtn: ['Cancel', 'Annulla'],
  resetConfirm: ['Erase ALL meta progress — bank, unlocks, achievements, stats. This cannot be undone.', 'Cancellare TUTTI i progressi: banca, sblocchi, obiettivi, statistiche. Non si può annullare.'],
  resetYes: ['Erase save', 'Cancella tutto'],
  screenShakeOn: ['Screen shake: ON', 'Scossa schermo: SÌ'],
  screenShakeOff: ['Screen shake: OFF', 'Scossa schermo: NO'],
  time: ['Time', 'Tempo'],
  level: ['Level', 'Livello'],
  kills: ['Kills', 'Uccisioni'],
  gold: ['Gold', 'Oro'],
  victory: ['VICTORY!', 'VITTORIA!'],
  youDied: ['YOU DIED', 'SEI MORTO'],
  victorySub: ['You survived the swarm.', 'Sei sopravvissuto allo sciame.'],
  deathSub: ['The horde got you.', 'L’orda ti ha preso.'],
  survived: ['Survived', 'Sopravvissuto'],
  levelReached: ['Level reached', 'Livello raggiunto'],
  bossesSlain: ['Bosses slain', 'Boss uccisi'],
  biggestHit: ['Biggest hit', 'Colpo più forte'],
  goldBanked: ['Gold banked', 'Oro in banca'],
  unlockedLabel: ['Unlocked:', 'Sbloccato:'],
  unlockToast: ['UNLOCKED: {0}', 'SBLOCCATO: {0}'],
  playAgain: ['Play again', 'Gioca ancora'],
  mainMenu: ['Main menu', 'Menu principale'],
  // end-of-run recap (#missed7)
  recapBuild: ['Your build', 'La tua build'],
  recapDamage: ['Damage by weapon', 'Danni per arma'],
  recapTopWeapon: ['MVP', 'MVP'],
  recapKilledBy: ['Killed by', 'Ucciso da'],
  recapEffects: ['Effects', 'Effetti'],
  recapAura: ['Aura', 'Aura'],
  recapOrbit: ['Orbit', 'Orbita'],
  recapVolley: ['Volley', 'Raffica'],
  recapHarvest: ['Soul Harvest', 'Raccolta di Anime'],
  recapTheSwarm: ['the swarm', 'lo sciame'],
  dash: ['⇧ SLIDE', '⇧ SCIVOLATA'],
  dashPad: ['Ⓑ SLIDE · Ⓐ JUMP', 'Ⓑ SCIVOLATA · Ⓐ SALTO'],
  foes: ['foes', 'nemici'],
  tag_new: ['NEW', 'NUOVO'],
  tag_evolve: ['EVOLVE', 'EVOLVI'],
  tag_heal: ['HEAL', 'CURA'],
  tag_gold: ['GOLD', 'ORO'],
  // draft-card category ribbons (clarity pass) — so a tome reads as a tome even on a tome-UP card, distinct from items
  cat_weapon: ['WEAPON', 'ARMA'],
  cat_item: ['ITEM', 'OGGETTO'],
  cat_tome: ['TOME', 'TOMO'],
  cat_evolve: ['EVOLUTION', 'EVOLUZIONE'],
  cap_full: ['FULL', 'PIENO'], // loadout-row cue shown when a category hit its cap (only upgrades from here)
  cap_full_tip: ['Full — only upgrades to existing picks from here', 'Pieno — d\'ora in poi solo potenziamenti di ciò che hai'],
  // evolution-readiness HUD cue (#missed4)
  evoReady: ['READY TO EVOLVE', 'PRONTO A EVOLVERE'],
  evoReadyToast: ['✦ READY TO EVOLVE', '✦ PRONTO A EVOLVERE'],
  evoNeedsItem: ['needs', 'richiede'],
  evoNeedsMax: ['level up to max', 'porta al livello max'],
  feast: ['Feast', 'Banchetto'],
  feastDesc: ['Restore 40% of your max HP.', 'Recupera il 40% degli HP massimi.'],
  coinStash: ['Coin Stash', 'Gruzzolo'],
  coinStashDesc: ['Gain a pile of gold for the meta shop.', 'Ottieni un mucchio d’oro per il santuario.'],
  // toasts
  bossIncoming: ['BOSS INCOMING', 'ARRIVA UN BOSS'],
  bossSlain: ['Boss slain!', 'Boss ucciso!'],
  eliteAppeared: ['Elite appeared', 'È apparso un élite'],
  // run-shape beats (#missed8)
  miniBossIncoming: ['★ CHAMPION', '★ CAMPIONE'],
  miniBossToast: ['★ A champion approaches!', '★ Arriva un campione!'],
  miniBossDown: ['★ Champion down!', '★ Campione abbattuto!'],
  surgeIncoming: ['SWARM INCOMING', 'ONDATA IN ARRIVO'],
  revived: ['Revived!', 'Resuscitato!'],
  vendorAppeared: ['A vendor appeared!', 'È apparso un mercante!'],
  shrineMagnet: ['All loot pulled in!', 'Tutto il bottino attratto!'],
  shrineGold: ['Gold windfall!', 'Pioggia d’oro!'],
  shrineHeal: ['Fully healed + shield!', 'Curato + scudo!'],
  shrineTreasure: ['Treasure! Two chests dropped', 'Tesoro! Due forzieri'],
  shrineBattle: ['Ambush! Loot left behind', 'Imboscata! Bottino in palio'],
  evolvedInto: ['Evolved into {0}!', 'Evoluto in {0}!'],
  obj_survive: ['Survive', 'Sopravvivi'],
  obj_elitehunt: ['Hunt the Elites', 'Caccia agli Elite'],
  obj_slay: ['Cull the Swarm', 'Stermina lo Sciame'],
  obj_portal: ['Reach the Boss Portal', 'Raggiungi il Portale del Boss'],
  obj_portal_channel: ['Activating portal… {0}%', 'Attivazione portale… {0}%'],
  portalAppeared: ['The Boss Portal has appeared!', 'È apparso il Portale del Boss!'],
  portalWarning: ['The portal is destabilizing — the boss is coming!', 'Il portale si destabilizza — il boss sta arrivando!'],
  obj_finalboss: ['Defeat the stage boss!', 'Sconfiggi il boss dello stage!'],
  finalbossToast: ['FINAL BOSS!', 'BOSS FINALE!'],
  chooseDifficulty: ['Choose difficulty', 'Scegli la difficoltà'],
  difficulty_sub: ['Higher difficulty = tougher swarms and better rewards.', 'Più difficoltà = sciami più duri e ricompense migliori.'],
  chooseTier: ['Choose tier', 'Scegli il tier'],
  tier: ['Tier', 'Tier'],
  tier_sub: ['Higher tiers chain more stages and start cursed — tougher, but richer loot. Clear a tier to unlock the next.', 'I tier alti concatenano più stage e iniziano maledetti — più duri, ma loot migliore. Completa un tier per sbloccare il successivo.'],
  tierDesc: ['{0}-stage gauntlet · tougher + cursed + richer loot', 'Sfida da {0} stage · più dura + maledetta + loot migliore'],
  tierCleared: ['Cleared', 'Completato'],
  tierLocked: ['Clear the previous tier to unlock', 'Completa il tier precedente per sbloccare'],
  stageClear: ['⚑ Stage clear! Next stage…', '⚑ Stage completato! Prossimo stage…'],
  stats_btn: ['STATS', 'STATISTICHE'],
  statsTitle: ['Records', 'Statistiche'],
  st_runs: ['Total runs', 'Partite totali'],
  st_kills: ['Total kills', 'Uccisioni totali'],
  st_bestTime: ['Best time', 'Miglior tempo'],
  st_bestLevel: ['Best level', 'Miglior livello'],
  st_bosses: ['Bosses slain', 'Boss uccisi'],
  st_gold: ['Total gold earned', 'Oro totale guadagnato'],
  st_cleared: ['Maps cleared', 'Mappe completate'],
  st_chars: ['Characters unlocked', 'Personaggi sbloccati'],
  close: ['Close', 'Chiudi'],
  settings_btn: ['SETTINGS', 'IMPOSTAZIONI'],
  settingsTitle: ['Settings', 'Impostazioni'],
  s_master: ['Master volume', 'Volume generale'],
  s_sfx: ['Sound effects', 'Effetti sonori'],
  s_music: ['Music', 'Musica'],
  s_shake: ['Screen shake', 'Scossa schermo'],
  s_dmgnum: ['Damage numbers', 'Numeri danno'],
  s_lang: ['Language', 'Lingua'],
  on: ['ON', 'SÌ'],
  off: ['OFF', 'NO'],
  achievements_btn: ['ACHIEVEMENTS', 'OBIETTIVI'],
  achievementsTitle: ['Achievements', 'Obiettivi'],
  achProgress: ['{0} / {1} unlocked', '{0} / {1} sbloccati'],
  achToast: ['Achievement: {0}', 'Obiettivo: {0}'],
  codex_btn: ['CODEX', 'CODEX'],
  codexTitle: ['Galactic Codex', 'Codex Galattico'],
  codexWeapons: ['Weapons', 'Armi'],
  codexItems: ['Items', 'Oggetti'],
  codexEnemies: ['Aliens', 'Alieni'],
  codexHeroes: ['Heroes', 'Eroi'],
  extractBtn: ['Extract (win)', 'Estrazione (vinci)'],
  endlessBtn: ['Continue Endless', 'Continua all’infinito'],
  victoryChoiceSub: ['Stage cleared! Extract now, or push your luck endlessly?', 'Stage completato! Estrai ora, o tenta la sorte all’infinito?'],
  objEndless: ['ENDLESS — survive!', 'INFINITO — sopravvivi!'],
  endlessToast: ['ENDLESS MODE', 'MODALITÀ INFINITA'],
  shrineCurse: ['Curse raised — more aliens, more loot!', 'Curse +1 — più alieni, più loot!'],
  shrineCharge: ['SURGE! +50% damage for a while', 'SURGE! +50% danno per un po\''],
  tome_tag: ['TOME', 'TOMO'],
  codexTomes: ['Tomes', 'Tomi'],
  chestTitle: ['Supply Pod!', 'Capsula di Rifornimenti!'],
  chestTake: ['Take', 'Prendi'],
  chestGamble: ['Gamble · {0}', 'Scommetti · {0}'],
  challenges_btn: ['CHALLENGES', 'SFIDE'],
  challengesTitle: ['Challenges', 'Sfide'],
  challenge_sub: ['Pick a modifier for a tougher, spicier run.', 'Scegli un modificatore per una partita più tosta.'],
  challengeDone: ['✓ Challenge complete!', '✓ Sfida completata!'],
  challengeActive: ['Challenge: {0}', 'Sfida: {0}'],
  moaiHeal: ['The Moai heals you!', 'Il Moai ti cura!'],
  moaiGold: ['The Moai grants gold!', 'Il Moai ti dona oro!'],
  moaiBanish: ['The Moai grants +2 banish!', 'Il Moai dona +2 bandisci!'],
  moaiChest: ['The Moai gifts a pod!', 'Il Moai regala una capsula!'],
  microwaveDup: ['Duplicated {0}!', 'Duplicato {0}!'],
  microwaveSwap: ['Exchanged for {0}!', 'Scambiato con {0}!'],
  microwaveNone: ['Nothing to duplicate — gold instead', 'Niente da duplicare — oro'],
  microwaveTitle: ['Microwave', 'Microonde'],
  microwavePick: ['Choose an item to DUPLICATE — a same-rarity item is sacrificed', 'Scegli un oggetto da DUPLICARE — un oggetto della stessa rarità verrà sacrificato'],
  microwaveCancel: ['Cancel (+50 gold)', 'Annulla (+50 oro)'],
  microwaveDupDesc: ['Level up to {0} (sacrifices another item)', 'Sale al livello {0} (sacrifica un altro oggetto)'],
  microwaveSac: ['Duplicated {0} · sacrificed {1}', 'Duplicato {0} · sacrificato {1}'],
  lockCardTip: ['Lock — keep this card next level-up', 'Blocca — tieni questa carta al prossimo livello'],
  banishTip: ['Banish from this run ({0} left)', 'Bandisci da questa partita ({0} rimasti)'],
  lockRerollTip: ['Lock — keep across reroll', 'Blocca — tieni dopo la ripescata'],
  gamepadConnected: ['Gamepad connected', 'Controller collegato'],
}

export function t(key: string, ...args: (string | number)[]): string {
  const e = CHROME[key]
  let s = e ? e[_lang === 'it' ? 1 : 0] : key
  for (let i = 0; i < args.length; i++) s = s.replace(`{${i}}`, String(args[i]))
  return s
}

// ---- Content translations ----
type Cat = 'weapon' | 'item' | 'char' | 'map' | 'boss' | 'meta' | 'difficulty' | 'ach' | 'enemy' | 'tome' | 'challenge'
interface CE {
  name?: string
  desc?: string | ((l: number) => string)
  blurb?: string
  passive?: string
  hint?: string // localized unlock hint (char/map select) — falls back to the EN def hint via tHint()
}

const C: Record<Cat, Record<string, CE>> = {
  weapon: {
    boomeraxe: { name: 'Accetta Orbitale', desc: 'Lancia un’accetta che torna come boomerang, trapassando tutto. +1 al lv 3/6.' },
    revolver: { name: 'Sparastelle', desc: 'Spara dardi rapidi all’alieno più vicino. Più colpi e perforazione salendo di livello.' },
    halo: { name: 'Anello di Saturno', desc: 'Orbe che ti ruotano attorno, frantumando ciò che toccano. Più orbe salendo.' },
    frostpulse: { name: 'Crio-Impulso', desc: 'Un’onda criogenica danneggia e rallenta gli alieni vicini. Il raggio cresce.' },
    meteor: { name: 'Pioggia di Meteore', desc: 'Una meteora precipita su un alieno a caso; esplode con grande danno ad area e ustione.' },
    zapper: { name: 'Folgora-Arco', desc: 'Un fulmine colpisce l’alieno più vicino e rimbalza sugli altri.' },
    sawblade: { name: 'Sega al Plasma', desc: 'Seghe di plasma ti girano attorno, falciando lo sciame.' },
    scattergun: { name: 'Cosmo-Rosa', desc: 'Spara un cono di pallini al plasma. Devastante da vicino.' },
    drones: { name: 'Folletti-Sentinella', desc: 'Lancia mini-droni a ricerca che inseguono gli alieni.' },
    tome: { name: 'Codice Cosmico', desc: 'Un codice stellare ti orbita, colpendo con dardi di luce ad alto critico.' },
    rotaura: { name: 'Aura Pulsar', desc: 'Un campo di energia rotante ti avvolge, avvelenando tutto attorno.' },
    lash: { name: 'Frusta-Tentacolo', desc: 'Schiocca una frusta aliena nella direzione che guardi; arco ampio, in due versi al lv 5.' },
    caltrops: { name: 'Mine-Spora', desc: 'Sparge spine-spora che pungono gli alieni che ci passano.' },
    ricochet: { name: 'Dardo Rimbalzino', desc: 'Lancia un dardo che carambola tra gli alieni. Più rimbalzi salendo.' },
    singularity: { name: 'Micro-Singolarità', desc: 'Apre una singolarità che risucchia gli alieni e li schiaccia.' },
    railgun: { name: 'Rail-Lancia', desc: 'Un colpo a rotaia che trapassa tutti gli alieni in linea. Forte ma lento.' },
    sporecloud: { name: 'Nube di Spore', desc: 'Sprigiona una nube tossica persistente che avvelena chi è dentro.' },
    frostlance: { name: 'Crio-Lancia', desc: 'Scaglia lance di ghiaccio perforanti che rallentano e congelano brevemente.' },
    stormglaive: { name: 'Glaive della Tempesta', desc: 'EVOLUTA: una tempesta di glaive che ruotano e tornano, incatenando fulmini.' },
    gatling: { name: 'Gatling Stellare', desc: 'EVOLUTA: una grandine incessante di proiettili infuocati.' },
    ringofsaturn: { name: 'Anelli di Saturno', desc: 'EVOLUTA: un anello enorme di orbe che annienta tutto attorno a te.' },
    blizzard: { name: 'Crio-Bufera', desc: 'EVOLUTA: una tempesta di ghiaccio perenne che congela e dilania i vicini.' },
    apocalypse: { name: 'Apocalisse di Meteore', desc: 'EVOLUTA: una pioggia incessante di meteore.' },
    tempest: { name: 'Tempesta Ionica', desc: 'EVOLUTA: fulmini a catena che attraversano l’intero campo.' },
    hydralash: { name: 'Tentacoli d’Idra', desc: 'EVOLUTA: tentacoli in ogni direzione a velocità accecante.' },
    minefield: { name: 'Campo di Mine-Spora', desc: 'EVOLUTA: ricopre il terreno di mine che esplodono a catena.' },
    pinball: { name: 'Flipper Cosmico', desc: 'EVOLUTA: una raffica di sfere che rimbalzano all’infinito sul campo.' },
    eventhorizon: { name: 'Orizzonte degli Eventi', desc: 'EVOLUTA: un immenso buco nero che inghiotte e annienta lo sciame.' },
    annihilator: { name: 'Rail-Annichilatore', desc: 'EVOLUTA: un triplo raggio a rotaia che vaporizza tutto in un ampio arco.' },
    miasma: { name: 'Miasma Tossico', desc: 'EVOLUTA: ricopre il campo di vaste nubi di morte persistenti.' },
    pestilence: { name: 'Pestilenza', desc: 'EVOLUTA: una vasta aura di peste che si espande e avvelena di continuo tutto il campo.' },
    glacialspear: { name: 'Lancia Glaciale', desc: 'EVOLUTA: una lancia di ghiaccio colossale che congela e frantuma ciò che trapassa.' },
    solarflare: { name: 'Brillamento Solare', desc: 'Un’aura solare rovente brucia gli alieni vicini e li incendia.' },
    novamortar: { name: 'Mortaio Nova', desc: 'Lancia un proiettile carico sull’alieno più coriaceo; esplode in un lampo accecante.' },
    prism: { name: 'Raggio Prisma', desc: 'Scompone la luce in raggi perforanti verso l’alieno più vicino. Più raggi salendo.' },
    quasarorb: { name: 'Orbe Quasar', desc: 'Rilascia un’orbe pesante che insegue lentamente, trapassando e schiacciando tutto.' },
    photonlance: { name: 'Lancia Fotonica', desc: 'Spara dardi di luce rapidi che perforano qualche alieno. Veloce e implacabile.' },
    acidspit: { name: 'Sputo Acido', desc: 'Sputa globi d’acido a ricerca che inseguono e corrodono con veleno.' },
    shocknova: { name: 'Onda d’Urto', desc: 'Un’onda cinetica esplode attorno a te, scaraventando indietro gli alieni.' },
    starshards: { name: 'Schegge Stellari', desc: 'Sprigiona schegge in ogni direzione che rimbalzano tra gli alieni.' },
    cryobomb: { name: 'Crio-Bomba', desc: 'Lancia un proiettile criogenico; esplode congelando e rallentando i nemici colpiti.' },
    plasmacutter: { name: 'Taglia-Plasma', desc: 'Lame di plasma roventi orbitano rapide e vicine, bruciando ciò che dilaniano.' },
    needler: { name: 'Aghi-Sciame', desc: 'Sputa un flusso rapido di aghi a ricerca verso l’alieno più vicino. Più aghi salendo di livello.' },
    warglaive: { name: 'Glaive da Guerra', desc: 'Lancia un ventaglio di glaive che vanno e tornano, trapassando l’intera orda. Più glaive salendo.' },
    flakcannon: { name: 'Cannone Flak', desc: 'Lancia un grappolo di proiettili flak sull’alieno più coriaceo; ognuno esplode in un piccolo lampo. Più colpi salendo.' },
    razorring: { name: 'Anello di Lame', desc: 'Un anello stretto di lame veloci orbita vicino, dilaniando tutto ciò che ti tocca. Più lame salendo.' },
    gustslash: { name: 'Fendente di Vento', desc: 'Sferra un’ampia mezzaluna di vento nella direzione che guardi, falciando l’intera prima linea. In due versi al lv 6.' },
    coronalmass: { name: 'Massa Coronale', desc: 'EVOLUTA: una vasta corona solare che incenerisce l’intera orda.' },
    supernova: { name: 'Supernova', desc: 'EVOLUTA: un grappolo di proiettili stellari piove in detonazioni a catena.' },
    spectrum: { name: 'Spettro', desc: 'EVOLUTA: uno spettro completo di raggi perforanti si irradia in ogni direzione.' },
    quasarstorm: { name: 'Tempesta Quasar', desc: 'EVOLUTA: una raffica di orbe schiaccianti che danno la caccia per tutto il campo.' },
    photonstorm: { name: 'Tempesta Fotonica', desc: 'EVOLUTA: un torrente infinito di dardi di luce che dilania tutto in linea.' },
    acidstorm: { name: 'Tempesta Acida', desc: 'EVOLUTA: uno sciame incessante di globi corrosivi che sciolgono l’orda.' },
    pulsewave: { name: 'Onda Pulsante', desc: 'EVOLUTA: un immenso impulso cinetico che appiattisce e disperde lo sciame.' },
    starfall: { name: 'Pioggia di Stelle', desc: 'EVOLUTA: una tempesta di schegge che rimbalzano all’infinito sul campo.' },
    absolutezero: { name: 'Zero Assoluto', desc: 'EVOLUTA: una raffica di proiettili criogenici imprigiona l’intero campo nel ghiaccio.' },
    plasmavortex: { name: 'Vortice di Plasma', desc: 'EVOLUTA: un vortice ruggente di lame di plasma che incenerisce tutto attorno.' },
  },
  item: {
    protein_shake: { name: 'Proteine', desc: (l) => `+12% danno per livello (ora +${12 * l}%).` },
    energy_drink: { name: 'Energetico', desc: (l) => `+10% velocità d’attacco per livello (ora +${10 * l}%).` },
    running_shoes: { name: 'Scarpe da Corsa', desc: (l) => `+8% velocità per livello (ora +${8 * l}%).` },
    big_lunch: { name: 'Pranzone', desc: (l) => `+20 HP massimi per livello (ora +${20 * l}).` },
    magnet: { name: 'Calamita', desc: (l) => `+45% raggio di raccolta per livello (ora +${45 * l}%).` },
    lucky_clover: { name: 'Quadrifoglio', desc: (l) => `+0.6 fortuna per livello — rarità e proc migliori.` },
    spyglass: { name: 'Cannocchiale', desc: (l) => `+8% probabilità critico e +0.15 danno critico per livello.` },
    wide_lens: { name: 'Lente Larga', desc: (l) => `+12% area per livello (ora +${12 * l}%).` },
    spring_coil: { name: 'Molla', desc: () => `+15% velocità proiettili e +10% durata per livello.` },
    cursed_doll: { name: 'Bambola Maledetta', desc: (l) => `+25% danno per livello, ma -10% HP massimi per livello.` },
    leech_ring: { name: 'Anello Sanguisuga', desc: (l) => `Cura il ${(2 * l).toFixed(0)}% del danno inflitto.` },
    iron_plate: { name: 'Piastra di Ferro', desc: (l) => `+4 armatura per livello — riduzione danno fissa.` },
    ghost_cloak: { name: 'Mantello Spettrale', desc: (l) => `+6% schivata per livello — chance di ignorare un colpo.` },
    thorn_mail: { name: 'Cotta di Spine', desc: (l) => `Rifletti il ${(25 * l).toFixed(0)}% del danno da contatto come spine.` },
    piggy_bank: { name: 'Salvadanaio', desc: (l) => `+25% oro guadagnato per livello.` },
    graduation_cap: { name: 'Tocco di Laurea', desc: (l) => `+20% XP guadagnata per livello.` },
    phoenix_feather: { name: 'Piuma di Fenice', desc: (l) => `Resuscita una volta per livello al 50% degli HP.` },
    heart_locket: { name: 'Medaglione', desc: (l) => `+1.2 rigenerazione HP al secondo per livello.` },
    gale_charm: { name: 'Amuleto del Vento', desc: (l) => `+10% velocità proiettili e +5% spinta per livello. (Evolve Accetta Orbitale)` },
    hot_sauce: { name: 'Salsa Piccante', desc: (l) => `+10% danno e +6% velocità d’attacco per livello. (Evolve Sparastelle)` },
    gyroscope: { name: 'Giroscopio', desc: (l) => `+1 proiettile per livello e +6% area. (Evolve Anello di Saturno)` },
    frozen_heart: { name: 'Cuore Gelato', desc: (l) => `+10% area e +4 armatura per livello. I nemici congelati si FRANTUMANO se colpiti. (Evolve Crio-Impulso)` },
    ember_core: { name: 'Nucleo di Brace', desc: (l) => `+15% danno, +8% area e +12% danno ai nemici afflitti per livello. I nemici in fiamme ESPLODONO morendo (nova di fuoco). (Evolve Pioggia di Meteore)` },
    capacitor: { name: 'Condensatore', desc: (l) => `+8% velocità d’attacco per livello; +1 proiettile al livello 2. I critici ARCANO un fulmine a un nemico vicino. (Evolve Folgora-Arco)` },
    biohazard: { name: 'Rischio Biologico', desc: (l) => `+10% danno e +8% area per livello. (Evolve Aura Pulsar)` },
    barbed_wire: { name: 'Filo Spinato', desc: (l) => `+8% danno e +12% spinta per livello. (Evolve Frusta-Tentacolo)` },
    gunpowder: { name: 'Polvere da Sparo', desc: (l) => `+12% danno e +8% area per livello. (Evolve Mine-Spora)` },
    rubber_ball: { name: 'Palla di Gomma', desc: (l) => `+12% velocità proiettili e +1 proiettile per livello. (Evolve Dardo Rimbalzino)` },
    dark_matter: { name: 'Materia Oscura', desc: (l) => `+10% area e +0.5 fortuna per livello. (Evolve Micro-Singolarità)` },
    tungsten_rod: { name: 'Barra di Tungsteno', desc: (l) => `+12% danno e +12% velocità proiettili per livello. (Evolve Rail-Lancia)` },
    fungal_spores: { name: 'Spore Fungine', desc: (l) => `+10% danno, +12% area e +12% danno ai nemici afflitti per livello. Le morti avvelenate diffondono il CONTAGIO. (Evolve Nube di Spore)` },
    permafrost: { name: 'Permafrost', desc: (l) => `+10% area e +12% durata effetti per livello. (Evolve Crio-Lancia)` },
    solar_panel: { name: 'Pannello Solare', desc: (l) => `+12% danno e +8% area per livello. (Evolve Brillamento Solare)` },
    fusion_core: { name: 'Nucleo a Fusione', desc: (l) => `+15% danno e +8% area per livello. (Evolve Mortaio Nova)` },
    crystal_lens: { name: 'Lente di Cristallo', desc: (l) => `+1 proiettile per livello e +8% velocità proiettili. (Evolve Raggio Prisma)` },
    event_shard: { name: 'Scheggia d’Evento', desc: (l) => `+12% danno e +8% area per livello. (Evolve Orbe Quasar)` },
    light_amp: { name: 'Amplificatore di Luce', desc: (l) => `+8% velocità d’attacco e +10% velocità proiettili per livello. (Evolve Lancia Fotonica)` },
    acid_vial: { name: 'Fiala d’Acido', desc: (l) => `+10% danno e +10% area per livello. (Evolve Sputo Acido)` },
    kinetic_cell: { name: 'Cella Cinetica', desc: (l) => `+8% danno e +15% spinta per livello. (Evolve Onda d’Urto)` },
    shard_matrix: { name: 'Matrice di Schegge', desc: (l) => `+1 proiettile per livello e +10% velocità proiettili. (Evolve Schegge Stellari)` },
    cryo_canister: { name: 'Crio-Bombola', desc: (l) => `+10% area e +12% durata effetti per livello. (Evolve Crio-Bomba)` },
    plasma_cell: { name: 'Cella al Plasma', desc: (l) => `+12% danno e +6% area per livello. (Evolve Taglia-Plasma)` },
    overclock_chip: { name: 'Chip Overclock', desc: (l) => `+7% riduzione ricarica per livello — tutte le armi sparano più rapide.` },
    targeting_array: { name: 'Array di Mira', desc: (l) => `+1 proiettile ogni 2 livelli e +8% velocità proiettili per livello.` },
    recoil_booster: { name: 'Potenzia-Rinculo', desc: (l) => `+10% spinta e +6% danno per livello.` },
    nano_coating: { name: 'Nano-Rivestimento', desc: (l) => `+3 armatura e +12 HP massimi per livello.` },
    focusing_array: { name: 'Array di Focalizzazione', desc: (l) => `+10% area e +8% danno per livello.` },
    adrenal_implant: { name: 'Impianto Adrenalinico', desc: (l) => `+7% velocità e +8% velocità d’attacco per livello.` },
    vampiric_fang: { name: 'Zanna Vampirica', desc: (l) => `Cura il ${(2.5 * l).toFixed(1)}% del danno inflitto e +6% danno per livello.` },
    quantum_battery: { name: 'Batteria Quantica', desc: (l) => `+12% durata effetti e +8% area per livello.` },
    star_chart: { name: 'Mappa Stellare', desc: (l) => `+0.5 fortuna e +18% oro guadagnato per livello.` },
    combat_stims: { name: 'Stimolanti da Combattimento', desc: (l) => `+6% critico, +0.12 danno critico e +5% velocità d’attacco per livello.` },
    reinforced_hull: { name: 'Scafo Rinforzato', desc: (l) => `+18 HP massimi, +2 armatura e +0.8 rigenerazione HP per livello.` },
    scavenger_drone: { name: 'Drone Recuperatore', desc: (l) => `+40% raggio di raccolta e +15% XP per livello.` },
    warp_boots: { name: 'Stivali Warp', desc: (l) => `+8% velocità e +5% schivata per livello.` },
    glass_reactor: { name: 'Reattore di Vetro', desc: (l) => `+30% danno per livello, ma -8% HP massimi per livello.` },
    kinetic_barrier: { name: 'Barriera Cinetica', desc: (l) => `Rifletti il ${(20 * l).toFixed(0)}% del danno da contatto e +5% schivata per livello.` },
    ion_thrusters: { name: 'Propulsori a Ioni', desc: (l) => `+14% velocità proiettili e +6% velocità d’attacco per livello.` },
    gravity_anchor: { name: 'Ancora Gravitazionale', desc: (l) => `+16 HP massimi e +14% spinta per livello.` },
    singularity_core: { name: 'Nucleo di Singolarità', desc: (l) => `+12% danno, +8% area e +0.4 fortuna per livello.` },
    volatile_rounds: { name: 'Munizioni Volatili', desc: (l) => `${6 + 2 * l}% prob. a ogni colpo di innescare una piccola esplosione. +4% danno per livello.` },
    static_discharge: { name: 'Scarica Statica', desc: (l) => `I critici incatenano fulmini a ${1 + l} alieni vicini. +4% prob. critico per livello.` },
    soul_harvest: { name: 'Raccolta di Anime', desc: (l) => `Ogni ${Math.max(8, 26 - 6 * l)} uccisioni lancia una nuke a ricerca. +10% XP per livello.` },
    critical_mass: { name: 'Massa Critica', desc: (l) => `Ogni critico accumula +2% danno (max +30%), azzerato dopo 4s senza critici. +0.1 danno critico per livello.` },
    incinerator: { name: 'Inceneritore', desc: (l) => `${8 + 3 * l}% prob. a ogni colpo di incendiare il bersaglio. +5% danno per livello.` },
    reapers_toll: { name: 'Pedaggio del Mietitore', desc: (l) => `${8 + 2 * l}% prob. a ogni uccisione di curarti e far esplodere gli alieni vicini. +1.5% rubavita per livello.` },
    // v11 — linea difensiva a scudo
    aegis_capacitor: { name: 'Condensatore Egida', desc: (l) => `+20 scudo e +2 armatura per livello — lo scudo si ricarica fuori dalla mischia.` },
    barrier_field: { name: 'Campo di Barriera', desc: (l) => `+16 scudo e +4% schivata per livello.` },
    deflector_array: { name: 'Deflettore', desc: (l) => `+18 scudo e rifletti il ${(15 * l).toFixed(0)}% del danno da contatto per livello.` },
    overshield_core: { name: 'Nucleo Sovra-Scudo', desc: (l) => `+40 scudo per livello, ma -5% velocità per livello.` },
    cryo_plating: { name: 'Blindatura Crio', desc: (l) => `+12 scudo, +2 armatura e +8% durata effetti per livello.` },
    // v11 — passivi ibridi
    titan_serum: { name: 'Siero Titano', desc: (l) => `+22 HP massimi e +6% danno per livello.` },
    berserker_chip: { name: 'Chip Berserker', desc: (l) => `+9% danno e +7% velocità d’attacco per livello, ma -2 armatura per livello.` },
    predator_optics: { name: 'Ottica Predatrice', desc: (l) => `+6% probabilità critico e +0.18 danno critico per livello.` },
    siege_battery: { name: 'Batteria d’Assedio', desc: (l) => `+10% area e +12% contraccolpo per livello.` },
    arc_reactor: { name: 'Reattore ad Arco', desc: (l) => `+8% velocità d’attacco e +1 rigenerazione HP/sec per livello.` },
    blood_chalice: { name: 'Calice di Sangue', desc: (l) => `Cura il ${(2 * l).toFixed(0)}% del danno inflitto e +16 HP massimi per livello.` },
    windrunner_cloak: { name: 'Mantello del Vento', desc: (l) => `+7% velocità e +6% velocità d’attacco per livello.` },
    fortune_idol: { name: 'Idolo della Fortuna', desc: (l) => `+0.5 fortuna, +15% oro e +12% PE per livello.` },
    neural_link: { name: 'Collegamento Neurale', desc: (l) => `+6% riduzione ricarica e +4% probabilità critico per livello.` },
    juggernaut_core: { name: 'Nucleo Juggernaut', desc: (l) => `+26 HP massimi e +3 armatura per livello, ma -4% velocità per livello.` },
    venom_gland: { name: 'Ghiandola Velenosa', desc: (l) => `+10% danno agli afflitti e +6% danno per livello.` },
    glass_lens: { name: 'Lente di Vetro', desc: (l) => `+8% probabilità critico e +0.2 danno critico per livello, ma -6% HP massimi per livello.` },
    swarm_capacitor: { name: 'Condensatore Sciame', desc: (l) => `+1 proiettile ogni 2 livelli e +10% velocità proiettili per livello.` },
    phoenix_core: { name: 'Nucleo Fenice', desc: () => `Risorgi una volta a partita con il 50% degli HP, e +2 rigenerazione HP/sec.` },
    duelist_gloves: { name: 'Guanti del Duellante', desc: (l) => `+8% velocità d’attacco e +5% probabilità critico per livello.` },
    gravity_lens: { name: 'Lente Gravitazionale', desc: (l) => `+12% area e +10% durata effetti per livello.` },
    prospector_lamp: { name: 'Lampada del Cercatore', desc: (l) => `+0.6 fortuna e +35% raggio di raccolta per livello.` },
    warlords_banner: { name: 'Stendardo del Signore della Guerra', desc: (l) => `+8% danno, +5% velocità d’attacco e +6% area per livello.` },
    sentinel_plating: { name: 'Blindatura Sentinella', desc: (l) => `+3 armatura, +14 scudo e +14 HP massimi per livello.` },
    // v11 — oggetti proc / innesco
    cryo_rounds: { name: 'Munizioni Crio', desc: (l) => `${10 + 3 * l}% prob. a ogni colpo di gelare — rallenta e congela brevemente. +6% velocità proiettili per livello.` },
    soul_siphon: { name: 'Sifone d’Anime', desc: (l) => `A ogni uccisione, raccogli +${1 + l} scudo (fino al tuo massimo). +10 scudo per livello.` },
    overcharger: { name: 'Sovraccaricatore', desc: (l) => `${12 + 4 * l}% prob. a ogni critico di scaricare un arco a catena e uno scoppio. +5% probabilità critico per livello.` },
    cluster_bombs: { name: 'Bombe a Grappolo', desc: (l) => `${12 + 3 * l}% prob. a ogni uccisione di far scoppiare un grappolo infuocato. +5% area per livello.` },
    ember_rounds: { name: 'Munizioni Brace', desc: (l) => `${8 + 3 * l}% prob. a ogni colpo di incendiare il bersaglio. +5% danno agli afflitti per livello.` },
  },
  char: {
    bonker: { name: 'Bonker', blurb: 'Il tuttofare. Bonk affidabili, niente sorprese.', passive: 'Le uccisioni hanno una probabilità di sprigionare un’onda d’urto.' },
    pip: { name: 'Pip', blurb: 'Piccolo e veloce. Fragile ma rapido, spara all’impazzata.', passive: 'Spara periodicamente una raffica di proiettili rimbalzanti sui nemici vicini.' },
    tank: { name: 'Brick', blurb: 'Un muro di muscoli. Lento, robusto, picchia duro.', passive: 'Sempre più tosto: +0.5 armatura per livello.' },
    witch: { name: 'Hazel', blurb: 'Maga di vetro. Danno enorme, sottile come carta.', passive: 'Scatena periodicamente una nova arcana che esplode intorno a te.', hint: 'Raggiungi il livello 15 in una partita.' },
    frost: { name: 'Yuki', blurb: 'Domina il gelo. Rallenta l’orda fino a fermarla.', passive: 'Un’aura di gelo permanente danneggia e rallenta gli alieni vicini.', hint: 'Sopravvivi 5 minuti in una partita.' },
    gambler: { name: 'Lady Luck', blurb: 'Vive di fortuna. Grande luck, grandi critici, grande rischio.', passive: 'La fortuna si accumula: +0.06 fortuna per livello.', hint: 'Accumula 1000 oro in totale tra le partite.' },
    vampire: { name: 'Vlad', blurb: 'Si nutre dei caduti. Si cura un po’ a ogni uccisione.', passive: 'Cura 1 HP per uccisione (scala con gli HP max).', hint: 'Sconfiggi il tuo primo boss.' },
    berserker: { name: 'Ragnar', blurb: 'Più è ferito, più picchia forte. Vive sul filo.', passive: 'Fino a +70% danno man mano che gli HP calano.', hint: 'Raggiungi il livello 20 in una partita.' },
    engineer: { name: 'Dot', blurb: 'Più piombo. Cede potenza grezza per un proiettile in più su tutto.', passive: 'Lancia periodicamente un colpo di mortaio esplosivo su un nemico vicino.', hint: 'Accumula 2500 oro in totale tra le partite.' },
    monk: { name: 'Kai', blurb: 'Combattente agile. Veloce, evasivo, implacabile.', passive: 'Sempre più sfuggente: +0.4% schivata per livello.', hint: 'Sopravvivi 8 minuti in una partita.' },
    gunner: { name: 'Tex', blurb: 'Inferno di proiettili. Spara veloce e largo, ma leggero.', passive: 'A intervalli scarica una raffica di proiettili tutt’attorno.', hint: 'Uccidi 3000 nemici in totale tra le partite.' },
    juggernaut: { name: 'Atlas', blurb: 'Un mech-fortezza ambulante. Lento, immenso, incassa colpi.', passive: 'La corazza pesante assorbe il 30% di tutti i danni subiti.', hint: 'Sopravvivi 10 minuti in una partita.' },
    glasscannon: { name: 'Nova', blurb: 'Un reattore in tuta spaziale. Potenza devastante, scudo quasi nullo.', passive: '+40% danno inflitto, ma +60% danno subito.', hint: 'Infliggi 200 danni in un solo colpo.' },
    executioner: { name: 'Reaper', blurb: 'Mietitore dei morenti. Finisce in un colpo gli alieni indeboliti.', passive: '+75% danno ai nemici sotto il 30% degli HP.', hint: 'Uccidi 5 boss in totale tra le partite.' },
    swarm: { name: 'Echo', blurb: 'Unità drone da controllo-sciame. Inonda il campo di proiettili.', passive: 'Due droni d’attacco ti orbitano in permanenza, falciando ciò che toccano.', hint: 'Possiedi 5 armi contemporaneamente in una partita.' },
    sustain: { name: 'Pulsar', blurb: 'Eso-tuta auto-riparante. Sopravvive a tutto per logoramento.', passive: 'Impulso di auto-riparazione periodico: cura e dà un piccolo scudo.', hint: 'Accumula 5000 oro in totale tra le partite.' },
    marksman: { name: 'Vesper', blurb: 'Una cecchina infallibile. Le sue armi sparano dove mira — punta e spara.', passive: 'Mira manuale: le tue armi sparano dove punti (verso il tuo orientamento), non solo sull’alieno più vicino. +precisione critica.', hint: 'Infliggi 500 danni con un solo colpo in una partita.' },
    cinder: { name: 'Ember', blurb: 'Una piromante che vive per il rogo. Gli afflitti si sciolgono sotto di lei.', passive: 'Gli alieni afflitti subiscono sempre di più: +25% danno da stato, che cresce dell’1.2% per livello.', hint: 'Accumula 8000 oro in totale tra le partite.' },
    bastion: { name: 'Bastion', blurb: 'Un eso-scheletro baluardo. Si nasconde dietro uno scudo d’energia rigenerante inarrestabile.', passive: 'Inizia con un grande scudo rigenerante: +60 scudo, che cresce di +6 per livello.', hint: 'Sopravvivi 12 minuti in una partita.' },
    dicer: { name: 'Roll', blurb: 'Una scommessa vivente. Ogni livello tira un potenziamento permanente casuale — nessuna partita uguale.', passive: 'Ogni salita di livello tira uno di sei potenziamenti permanenti (potenza / cadenza / critico / x2 / HP / armatura).', hint: 'Raggiungi il livello 25 in una partita.' },
    forager: { name: 'Stash', blurb: 'Uno spazzino instancabile. Continua a dissotterrare oro gratis mentre gli altri combattono.', passive: 'Dissotterra periodicamente una riserva di oro gratis vicino a te.', hint: 'Accumula 12000 oro in totale tra le partite.' },
  },
  map: {
    meadow: { name: 'Distese della Quiete', blurb: 'Polverose pianure lunari grigie. Un posto tranquillo per imparare a bonkare gli alieni.' },
    dunes: { name: 'Le Dune Rugginose', blurb: 'Dune marziane rosso-ruggine infinite e cose che mordono. Porta ossigeno.', hint: 'Completa Distese della Quiete (sconfiggi il boss).' },
    graveyard: { name: 'Cimitero Orbitale', blurb: 'Il relitto alla deriva di una stazione. L’equipaggio morto non riposa.', hint: 'Completa Le Dune Rugginose.' },
    rift: { name: 'Le Fauci del Vuoto', blurb: 'Uno squarcio nello spaziotempo. Solo i più forti sopravvivono allo sciame.', hint: 'Completa Cimitero Orbitale.' },
  },
  ach: {
    first_blood: { name: 'Primo Sangue', blurb: 'Uccidi il tuo primo nemico.' },
    centurion: { name: 'Centurione', blurb: 'Uccidi 100 nemici in totale.' },
    slayer: { name: 'Massacratore', blurb: 'Uccidi 1.000 nemici in totale.' },
    exterminator: { name: 'Sterminatore', blurb: 'Uccidi 10.000 nemici in totale.' },
    survivor: { name: 'Sopravvissuto', blurb: 'Sopravvivi 5 minuti in una partita.' },
    marathoner: { name: 'Maratoneta', blurb: 'Sopravvivi 10 minuti in una partita.' },
    level10: { name: 'In Forma', blurb: 'Raggiungi il livello 10 in una partita.' },
    level20: { name: 'Strapotente', blurb: 'Raggiungi il livello 20 in una partita.' },
    level30: { name: 'Inarrestabile', blurb: 'Raggiungi il livello 30 in una partita.' },
    boss1: { name: 'Ammazzagiganti', blurb: 'Sconfiggi il tuo primo boss.' },
    boss10: { name: 'Cacciatore di Boss', blurb: 'Sconfiggi 10 boss in totale.' },
    boss25: { name: 'Flagello dei Boss', blurb: 'Sconfiggi 25 boss in totale.' },
    clear_meadow: { name: 'Prato Completato', blurb: 'Batti le Distese della Quiete.' },
    clear_dunes: { name: 'Dune Completate', blurb: 'Batti le Dune Rugginose.' },
    clear_graveyard: { name: 'Cimitero Completato', blurb: 'Batti il Cimitero Orbitale.' },
    clear_rift: { name: 'Frattura Completata', blurb: 'Batti le Fauci del Vuoto.' },
    world_tour: { name: 'Giro del Mondo', blurb: 'Completa ogni mappa.' },
    hard_clear: { name: 'Modalità Difficile', blurb: 'Completa una mappa a Difficile o oltre.' },
    cooked_clear: { name: 'Ben Cotto', blurb: 'Completa una mappa a Estremo.' },
    rich: { name: 'Pieno di Soldi', blurb: 'Guadagna 1.000 oro in totale.' },
    tycoon: { name: 'Magnate', blurb: 'Guadagna 5.000 oro in totale.' },
    first_evo: { name: 'Evoluto', blurb: 'Evolvi un’arma.' },
    evo_master: { name: 'Apice', blurb: 'Evolvi 10 armi in totale.' },
    recruiter: { name: 'Reclutatore', blurb: 'Sblocca 8 personaggi.' },
    full_roster: { name: 'Tutti in Campo', blurb: 'Sblocca ogni personaggio.' },
    regular: { name: 'Habitué', blurb: 'Gioca 10 partite.' },
    addicted: { name: 'Dipendente', blurb: 'Gioca 50 partite.' },
    level40: { name: 'Asceso', blurb: 'Raggiungi il livello 40 in una partita.' },
    kills25k: { name: 'Annichilatore', blurb: 'Uccidi 25.000 nemici in totale.' },
    boss50: { name: 'Leggenda dei Boss', blurb: 'Sconfiggi 50 boss in totale.' },
    veteran: { name: 'Veterano', blurb: 'Gioca 100 partite.' },
  },
  challenge: {
    double_bosses: { name: 'Assalto Boss', blurb: 'I boss appaiono il doppio delle volte. Spero tu abbia potenza di fuoco.' },
    glass_cannon: { name: 'Cannone di Vetro', blurb: 'Danno enorme, ma una frazione degli HP.' },
    swarm: { name: 'Sciame Infinito', blurb: 'L’orda è enorme fin dall’inizio.' },
    cursed_start: { name: 'Inizio Maledetto', blurb: 'Parti con +3 curse — più alieni, più boss, più loot.' },
    iron_man: { name: 'Iron Man', blurb: 'Nessuna cura di alcun tipo. Ogni colpo conta.' },
    speed_demon: { name: 'Demone della Velocità', blurb: 'Eroe velocissimo — ma anche lo sciame è più rapido e fitto.' },
  },
  difficulty: {
    easy: { name: 'Facile', blurb: 'Nemici meno numerosi e più deboli. Un bonk rilassato.' },
    normal: { name: 'Normale', blurb: 'La sfida pensata dagli autori.' },
    hard: { name: 'Difficile', blurb: 'Sciami più fitti e duri — bottino migliore.' },
    cooked: { name: 'Estremo', blurb: 'Morirai, probabilmente. Ricompense top.' },
  },
  tome: {
    might: { name: 'Tomo della Potenza', desc: (l) => `+10% danno per livello (ora +${10 * l}%).` },
    vigor: { name: 'Tomo del Vigore', desc: (l) => `+18 HP massimi per livello (ora +${18 * l}).` },
    haste: { name: 'Tomo della Rapidità', desc: () => `+6% velocità per livello.` },
    frenzy: { name: 'Tomo della Frenesia', desc: () => `+8% velocità d’attacco per livello.` },
    focus: { name: 'Tomo della Mira', desc: () => `+6% probabilità di critico per livello.` },
    ferocity: { name: 'Tomo della Ferocia', desc: () => `+0.2 danno critico per livello.` },
    reach: { name: 'Tomo della Portata', desc: () => `+10% area per livello.` },
    velocity: { name: 'Tomo della Velocità', desc: () => `+12% velocità proiettili per livello.` },
    volley: { name: 'Tomo della Salva', desc: () => `+1 proiettile per livello.` },
    duration: { name: 'Tomo della Durata', desc: () => `+10% durata effetti per livello.` },
    greed: { name: 'Tomo dell’Avidità', desc: () => `+20% oro guadagnato per livello.` },
    scholar: { name: 'Tomo dello Studioso', desc: () => `+15% XP guadagnata per livello.` },
    fortune: { name: 'Tomo della Fortuna', desc: () => `+0.5 fortuna per livello.` },
    bulwark: { name: 'Tomo del Baluardo', desc: () => `+4 armatura per livello.` },
    phantom: { name: 'Tomo del Fantasma', desc: () => `+5% schivata per livello.` },
    magnetism: { name: 'Tomo del Magnetismo', desc: () => `+30% raggio di raccolta per livello.` },
    vitality: { name: 'Tomo della Vitalità', desc: () => `+1.5 rigenerazione HP/sec per livello.` },
    leech: { name: 'Tomo della Sanguisuga', desc: () => `+1.5% rubavita per livello.` },
    tempo: { name: 'Tomo del Tempo', desc: () => `+4% riduzione ricarica per livello.` },
    force: { name: 'Tomo della Forza', desc: () => `+15% contraccolpo per livello.` },
    thorns: { name: 'Tomo delle Spine', desc: () => `Rifletti +5% del danno da contatto per livello.` },
    affliction: { name: 'Tomo dell’Afflizione', desc: () => `+12% danno ai nemici afflitti per livello.` },
    rebirth: { name: 'Tomo della Rinascita', desc: (l) => `+${l} rinascita/e per partita.` },
    onslaught: { name: 'Tomo dell’Assalto', desc: () => `+5% danno e +5% velocità d’attacco per livello.` },
    aegis: { name: 'Tomo dell’Egida', desc: () => `+2 armatura e +10 HP max per livello.` },
    plunder: { name: 'Tomo del Bottino', desc: () => `+10% oro e +10% XP per livello.` },
  },
  enemy: {
    grub: { name: 'Larvoide' },
    runt: { name: 'Sporello' },
    brute: { name: 'Bruto Gravoide' },
    spitter: { name: 'Sputacido' },
    dasher: { name: 'Drone Sfreccia' },
    blob: { name: 'Ammasso Melmoso' },
    blobling: { name: 'Melmino' },
    wisp: { name: 'Spettro al Plasma' },
    reaver: { name: 'Falciatore del Vuoto' },
    sniper: { name: 'Cecchino a Raggi' },
    swarmling: { name: 'Sciamite' },
    archer: { name: 'Drone Dardo' },
    bomber: { name: 'Baccello Volatile' },
    ghoul: { name: 'Guscio Errante' },
    stalker: { name: 'Predatore di Fase' },
    shielder: { name: 'Drone Bastione' },
    summoner: { name: 'Richiamo-Alveare' },
    crystalback: { name: 'Cristalloide' },
    shard: { name: 'Scheggino' },
    revenant: { name: 'Spettro Revenante' },
    scuttle: { name: 'Carapode Sgattaiolo' },
    locust: { name: 'Locusta Stellare' },
    spittoad: { name: 'Gracidatore Tossico' },
    imp: { name: 'Lancia-Detriti' },
    roomba: { name: 'Spazzino Ribelle' },
    boar: { name: 'Devastatore' },
    batcloud: { name: 'Nube Sciame' },
    magmabrute: { name: 'Colosso di Magma' },
    voodoo: { name: 'Lancia-Maledizioni' },
    ironlich: { name: 'Sentinella di Ferro' },
    yetibull: { name: 'Caricatore Glaciale' },
    bogtroll: { name: 'Titano di Fanghiglia' },
    aegis: { name: 'Falange Egida' },
    warchanter: { name: 'Cantore di Guerra' },
    overseer: { name: 'Sorvegliante dell’Alveare' },
    razorwing: { name: 'Aladirasoio' },
    flankwraith: { name: 'Spettro Aggirante' },
    mender: { name: 'Drone Curatore' },
    kamikaze: { name: 'Kamikaze Rasoio' },
    siegewalker: { name: 'Camminatore d’Assedio' },
    broodspawner: { name: 'Generatore di Covata' },
    wartotem: { name: 'Totem di Guerra' },
    bombrusher: { name: 'Bomba d’Assalto' },
    hexorb: { name: 'Orbe Maledetta' },
    titanooze: { name: 'Melma Titano' },
  },
  boss: {
    boss_golem: { name: 'Maciullatore Monolite' },
    boss_hydra: { name: 'Terrore Tentacolare' },
    boss_lich: { name: 'Supermente Zerox' },
    boss_queen: { name: 'Imperatrice della Covata' },
    boss_warlord: { name: 'Mecha Tirannìa X' },
    boss_rootzilla: { name: 'Radicilla' },
    boss_sandwyrm: { name: 'Vermesabbia Supremo' },
    boss_reaper: { name: 'Lo Streamer Tetro' },
    boss_forklift: { name: 'Goblin Muletto Campione' },
    boss_scorpion: { name: 'Mecha-Scorpione' },
    boss_fridge: { name: 'Il Frigorifero' },
    boss_djskel: { name: 'DJ Scheletron' },
    boss_voidlord: { name: 'Il Signore del Vuoto' },
    boss_devourer: { name: 'Il Divoratore di Mondi' },
  },
  meta: {
    might: { name: 'Potenza', desc: (l) => `+${5 * l}% danno in ogni partita.` },
    vigor: { name: 'Vigore', desc: (l) => `+${15 * l} HP massimi in ogni partita.` },
    haste: { name: 'Rapidità', desc: (l) => `+${3 * l}% velocità in ogni partita.` },
    frenzy: { name: 'Frenesia', desc: (l) => `+${4 * l}% velocità d’attacco in ogni partita.` },
    fortune: { name: 'Fortuna', desc: (l) => `+${0.5 * l} fortuna in ogni partita.` },
    scholar: { name: 'Studioso', desc: (l) => `+${6 * l}% XP in ogni partita.` },
    greed: { name: 'Avidità', desc: (l) => `+${8 * l}% oro in ogni partita.` },
    plating: { name: 'Corazza', desc: (l) => `+${2 * l} armatura in ogni partita.` },
    magnetism: { name: 'Magnetismo', desc: (l) => `+${10 * l}% raggio di raccolta in ogni partita.` },
    precision: { name: 'Precisione', desc: (l) => `+${2 * l}% probabilità critico in ogni partita.` },
    second_wind: { name: 'Secondo Respiro', desc: () => `Resuscita una volta per partita al 50% degli HP.` },
    ferocity: { name: 'Ferocia', desc: (l) => `+${(0.1 * l).toFixed(1)} danno critico in ogni partita.` },
    reach: { name: 'Portata', desc: (l) => `+${4 * l}% area d'effetto in ogni partita.` },
    velocity: { name: 'Velocità', desc: (l) => `+${5 * l}% velocità proiettili in ogni partita.` },
    vitality: { name: 'Vitalità', desc: (l) => `+${(0.5 * l).toFixed(1)} rigenerazione HP/sec in ogni partita.` },
    leech: { name: 'Sanguisuga', desc: (l) => `+${l}% rubavita in ogni partita.` },
    volley: { name: 'Raffica', desc: (l) => `+${l} proiettile in ogni partita.` },
  },
}

export function tName(cat: Cat, id: string, en: string): string {
  if (_lang === 'it') {
    const n = C[cat]?.[id]?.name
    if (n) return n
  }
  return en
}
export function tDesc(cat: Cat, id: string, level: number, enFn: (l: number) => string): string {
  if (_lang === 'it') {
    const d = C[cat]?.[id]?.desc
    if (typeof d === 'function') return d(level)
    if (typeof d === 'string') return d
  }
  return enFn(level)
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function tBlurb(cat: Cat, id: string, en: string): string {
  if (_lang === 'it') {
    const b = C[cat]?.[id]?.blurb
    if (b) return b
  }
  return en
}
export function tPassive(id: string, en: string): string {
  if (_lang === 'it') {
    const p = C.char?.[id]?.passive
    if (p) return p
  }
  return en
}
export function tHint(cat: Cat, id: string, en: string): string {
  if (_lang === 'it') {
    const h = C[cat]?.[id]?.hint
    if (h) return h
  }
  return en
}
